import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import { formatPlate, stripPlate } from '../utils/licensePlate.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import { formatDateOnly } from '../utils/date.ts';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  color: string;
  purchasePrice: number;
  sellingPrice?: number;
  status: 'in_stock' | 'sold' | 'reserved' | 'exchanged';
  purchaseDate: string;
  soldDate?: string;
  assetId: string;
}

const toneFor = (status: string): 'green' | 'amber' | 'slate' | 'blue' | 'red' => {
  if (status === 'in_stock') return 'green';
  if (status === 'reserved') return 'amber';
  if (status === 'sold') return 'slate';
  if (status === 'exchanged') return 'blue';
  return 'slate';
};

const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Vehicle['status']>('in_stock');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: vehicles, isLoading, error } = useQuery<Vehicle[]>(
    'vehicles',
    async () => (await api.get('/vehicles')).data,
    { staleTime: 1000 * 60 * 5 }
  );

  const filtered = useMemo(() => {
    return (vehicles || []).filter(v => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        v.make.toLowerCase().includes(q) ||
        v.model.toLowerCase().includes(q) ||
        formatPlate(v.licensePlate).toLowerCase().includes(q) ||
        stripPlate(v.licensePlate).toLowerCase().includes(q.replace(/[^a-z0-9]/gi, ''));
      const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [vehicles, searchTerm, statusFilter]);

  const stockValue = filtered
    .filter(v => v.status === 'in_stock' || v.status === 'reserved')
    .reduce((s, v) => {
      const n = Number(v.purchasePrice);
      return s + (Number.isFinite(n) ? n : 0);
    }, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar estoque.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Estoque p/ venda"
        title="Estoque p/ venda"
        subtitle="Inventário comercial · separado da Frota"
        actions={
          hasPermission('assets.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/vehicles/novo')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo veículo
            </button>
          ) : undefined
        }
      />

      <div className="dep-banner" style={{ marginBottom: 16 }}>
        <strong>Estoque ≠ Frota.</strong> Aqui ficam veículos à venda. Uso interno fica em Frota.
      </div>

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
              <option value="all">Todos</option>
              <option value="in_stock">Em estoque</option>
              <option value="reserved">Reservado</option>
              <option value="sold">Vendido</option>
              <option value="exchanged">Permutado</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Marca, modelo ou placa"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Veículos</div>
          <div className="kpi-value">{filtered.length}</div>
          <div className="kpi-hint">no filtro atual</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Custo de estoque</div>
          <div className="kpi-value">{formatCurrency(stockValue)}</div>
          <div className="kpi-hint">em estoque + reservados</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Em estoque</div>
          <div className="kpi-value">
            {(vehicles || []).filter(v => v.status === 'in_stock').length}
          </div>
          <div className="kpi-hint">disponíveis</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Inventário</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum veículo encontrado.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Veículo</th>
                  <th className="hide-m">Placa</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m">Compra</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Custo</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(v => (
                  <tr key={v.id}>
                    <td>
                      <StackCell
                        initials={`${(v.make || 'V')[0]}${(v.model || 'X')[0]}`.toUpperCase()}
                        title={`${v.make} ${v.model}`}
                        meta={`${v.year} · ${v.color || '—'}`}
                        stackExtra={
                          <>
                            {formatPlate(v.licensePlate)} · {formatStatus(v.status)}
                            {' · '}
                            {formatCurrency(Number.isFinite(Number(v.purchasePrice)) ? Number(v.purchasePrice) : 0)}
                            {v.purchaseDate ? ` · ${formatDateOnly(v.purchaseDate)}` : ''}
                          </>
                        }
                      />
                    </td>
                    <td className="mono hide-m">{formatPlate(v.licensePlate)}</td>
                    <td className="hide-m">
                      <StatusChip label={formatStatus(v.status)} tone={toneFor(v.status)} />
                    </td>
                    <td className="hide-m mono">
                      {v.purchaseDate
                        ? formatDateOnly(v.purchaseDate)
                        : '—'}
                    </td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number.isFinite(Number(v.purchasePrice)) ? Number(v.purchasePrice) : 0)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/vehicles/${v.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(v.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir veículo"
        message="Tem certeza que deseja excluir este veículo do estoque? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await api.delete(`/vehicles/${deleteId}`);
            queryClient.invalidateQueries(['vehicles']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Vehicles;
